<script setup lang="ts">
import { computed } from "vue";
import { Size, useDraggable } from "./useDraggable";
import type { Axis, DraggableProps } from "./useDraggable";
import "./DraggableWrapper.scss";

// Compose a new props type
type WrapperProps = DraggableProps & {
  size?: Size;
};
const props = withDefaults(defineProps<WrapperProps>(), {
  modelValue: null,
  defaultPosition: "center",
  axis: "both" as Axis,
  bounds: null,
  grid: null,
  handle: true, // default = drag from anywhere
  cancel: null,
  draggingDisabled: false,
  arrowKeyMovements: true,
  enableUserSelectHack: true,
  enableSizingToChild: true,
  zIndexWhileDragging: null,
  size: "medium" as Size,
  teleportTo: "#modals",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: { x: number; y: number }): void;
  (e: "dragstart", value: { x: number; y: number }, evt: PointerEvent): void;
  (e: "drag", value: { x: number; y: number }, evt: PointerEvent): void;
  (e: "dragend", value: { x: number; y: number }, evt: PointerEvent): void;
}>();

// Pass `props.handle` as a boolean flag to the composable
const { rootDraggable, styleObj, grabbing, useAbsolute, onKeyDown, rootChildSize } = useDraggable(
  props as unknown as DraggableProps,
  emit,
);

const sizeClass = computed(() => {
  if (!props.enableSizingToChild) {
    return `modal-${props.size}`;
  }

  return rootChildSize.value !== props.size && rootChildSize.value !== null
    ? `modal-${rootChildSize.value}`
    : `modal-${props.size}`;
});
</script>

<template>
  <component
    :is="props.backdrop ? 'div' : 'template'"
    :class="[{ backdrop: props.backdrop, 'background-locked': props.backgroundLocked }]"
  >
  </component>
  <div
    ref="rootDraggable"
    class="draggable-wrapper"
    :class="[
      {
        'dw-abs': useAbsolute,
        'has-handle': props.handle,
        'drag-disabled': props.draggingDisabled,
      },
      sizeClass,
    ]"
    :style="styleObj"
    :aria-grabbed="grabbing"
    role="group"
    :tabindex="0"
    data-root-draggable
    @keydown="onKeyDown"
  >
    <slot />
  </div>
</template>

<style lang="scss">
// NB not scoped so we can use Render.vue drawer-backdrop
.dw--draggable-item--present {
  .render-modal {
    cursor: not-allowed;
  }
  .drawer-container > .drawer-backdrop {
    background: rgba(33, 33, 33, 0.15);
  }

  // NB need to style any custom draggable element handle
  .draggable-wrapper:not(.drag-disabled) {
    &[aria-grabbed="false"] {
      .dw--draggable-handle {
        cursor: grab;
      }
    }
    &[aria-grabbed="true"] {
      .dw--draggable-handle {
        cursor: grabbing;
      }
    }

    box-shadow:
      0px 4px 6px rgba(0, 0, 0, 0.1),
      0px 2px 4px rgba(0, 0, 0, 0.1);
  }
}
</style>
